import { Component } from '@theme/component';

// A shared clock keeps paired announcements staggered after hover or tab changes.
const autoplayEpoch = performance.now();

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #abort;
  #reducedMotion;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;
  #startTimer = undefined;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.addEventListener('mouseenter', this.suspend, { signal });
    this.addEventListener('mouseleave', this.resume, { signal });
    this.addEventListener('focusin', this.suspend, { signal });
    this.addEventListener('focusout', this.resume, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    this.#reducedMotion.addEventListener('change', this.#handleMotionChange, { signal });
    this.current = this.#current;
    this.resume();
  }

  updatedCallback() {
    super.updatedCallback();
    this.current = this.#current;
    this.resume();
  }

  disconnectedCallback() {
    this.suspend();
    this.#abort?.abort();
    super.disconnectedCallback();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in milliseconds between slides.
   */
  play(interval = this.autoplayInterval) {
    this.suspend();
    this.paused = false;
    if (!this.autoplay || this.#reducedMotion?.matches || document.hidden || (this.refs.slides?.length ?? 0) < 2) return;

    const advance = () => {
      if (this.matches(':hover, :focus-within') || document.hidden) return;

      this.next();
    };
    const phase = Number.parseFloat(this.getAttribute('autoplay-phase'));
    if (Number.isFinite(phase)) {
      const firstChange = interval * (1 + Math.max(0, Math.min(phase, 1)));
      const elapsed = performance.now() - autoplayEpoch;
      const delay = elapsed < firstChange ? firstChange - elapsed : interval - ((elapsed - firstChange) % interval);
      this.#startTimer = setTimeout(() => {
        this.#startTimer = undefined;
        advance();
        this.#interval = setInterval(advance, interval);
      }, delay);
    } else {
      this.#interval = setInterval(advance, interval);
    }
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearTimeout(this.#startTimer);
    this.#startTimer = undefined;
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
      slide.inert = index !== relativeIndex;
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #handleMotionChange = () => (this.#reducedMotion.matches ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
